import { useCallback, useEffect, useState } from 'react';
import { Button, DialogShell, Spinner, cx } from '@timely/ui';
import { LIFECYCLE_LABELS, holds, type TenantContext } from '@timely/domain';
import { Field, FormNotice } from '../screens/AuthLayout';
import { useSession } from '../auth/SessionProvider';
import {
    LifecycleError, applyFreshStart, downloadCompanyExport, exportErrorMessage, formatWhen,
    previewFreshStart, reauthenticate, requestDeletion,
    type FreshStartPreview, type FreshStartResult,
} from './lifecycle';

const RESTORE_NOTE = 'The export is for portability and recovery assistance. Timely Payment cannot yet restore it automatically.';

/* ------------------------------------------------------------------ parts -- */

function CountList({ title, counts, tone }: { title: string; counts: [string, number][]; tone: 'dang' | 'neutral' }) {
    return (
        <section aria-label={title} className={cx('rounded-control px-4 py-3', tone === 'dang' ? 'bg-dang-bg' : 'bg-card-2')}>
            <h3 className={cx('text-secondary font-semibold mb-2', tone === 'dang' ? 'text-dang' : 'text-label')}>{title}</h3>
            <ul className="space-y-1">
                {counts.map(([key, n]) => (
                    <li key={key} className="flex justify-between gap-4 text-body text-label-2">
                        <span className={key === 'promises' ? 'pl-4' : ''}>{LIFECYCLE_LABELS[key]}</span>
                        <span className="tabular-nums font-semibold text-label" data-testid={`count-${key}`}>{n.toLocaleString()}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

/** Take an export now, or say plainly that you are going ahead without one. */
function ExportStep({
    context, exportId, exportedAt, onExported, skip, onSkip,
}: {
    context: TenantContext;
    exportId: string | null;
    exportedAt: string | null;
    onExported: (id: string | null) => void;
    skip: boolean;
    onSkip: (value: boolean) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const canExport = holds(context, 'workspace.export');

    const run = async () => {
        setBusy(true);
        setError(null);
        try {
            const { exportId: id } = await downloadCompanyExport(context.workspace.id);
            onExported(id);
        } catch (e) {
            setError(exportErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="rounded-control border border-separator-strong px-4 py-3.5" aria-label="Export first">
            <h3 className="text-body font-semibold text-label">1. Keep a copy first</h3>
            {exportId ? (
                <p className="text-body text-pos mt-1.5" data-testid="export-done">
                    Export downloaded{exportedAt ? ` ${formatWhen(exportedAt)}` : ''}. Keep the file somewhere safe.
                </p>
            ) : (
                <>
                    <p className="text-secondary text-label-2 mt-1.5 leading-relaxed">
                        Download a Company Data Export before you continue. {RESTORE_NOTE}
                    </p>
                    {error && <div className="mt-2"><FormNotice tone="error">{error}</FormNotice></div>}
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                        {canExport ? (
                            <Button variant="secondary" onClick={() => { void run(); }} disabled={busy} busy={busy}>
                                {busy ? 'Preparing the export…' : 'Download Company Data Export'}
                            </Button>
                        ) : (
                            <span className="text-secondary text-label-3">Only someone who may export company data can download one.</span>
                        )}
                    </div>
                    <label className="mt-3 flex items-start gap-2.5 text-secondary text-label-2 leading-snug">
                        <input
                            type="checkbox"
                            className="mt-0.5 h-4 w-4 accent-dang"
                            checked={skip}
                            onChange={e => onSkip(e.target.checked)}
                            data-testid="skip-export"
                        />
                        <span>Continue without an export. I understand Timely Payment cannot give this data back.</span>
                    </label>
                </>
            )}
        </section>
    );
}

/** The company name, typed; and, if the sign-in is not recent, the password. */
function ConfirmStep({
    companyName, name, onName, needsPassword, password, onPassword, step,
}: {
    companyName: string;
    name: string;
    onName: (v: string) => void;
    needsPassword: boolean;
    password: string;
    onPassword: (v: string) => void;
    step: number;
}) {
    return (
        <section aria-label="Confirm">
            <h3 className="text-body font-semibold text-label mb-2">{step}. Confirm</h3>
            <Field
                label={`Type the company name, ${companyName}, to confirm`}
                name="confirm-name"
                autoComplete="off"
                spellCheck={false}
                value={name}
                onChange={e => onName(e.target.value)}
            />
            {needsPassword && (
                <Field
                    label="Your password"
                    name="reauth-password"
                    type="password"
                    autoComplete="current-password"
                    hint="For your security, confirm it is you. You signed in more than ten minutes ago."
                    value={password}
                    onChange={e => onPassword(e.target.value)}
                />
            )}
        </section>
    );
}

/* ------------------------------------------------------------ Fresh Start -- */

export function FreshStartDialog({ context, onClose, onDone }: {
    context: TenantContext;
    onClose: () => void;
    onDone: (result: FreshStartResult) => void;
}) {
    const { email } = useSession();
    const [preview, setPreview] = useState<FreshStartPreview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [needsPassword, setNeedsPassword] = useState(false);
    const [exportId, setExportId] = useState<string | null>(null);
    const [exportedAt, setExportedAt] = useState<string | null>(null);
    const [skip, setSkip] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = await previewFreshStart(context.workspace.id);
            setPreview(p);
            setNeedsPassword(current => current || !p.recently_signed_in);
            if (p.recent_export) {
                setExportId(p.recent_export.export_id);
                setExportedAt(p.recent_export.finished_at);
            }
        } catch (e) {
            setError(e instanceof LifecycleError ? e.message : 'The preview could not be loaded. Try again.');
        } finally {
            setLoading(false);
        }
    }, [context.workspace.id]);

    useEffect(() => { void load(); }, [load]);

    const ready = !!preview && name === preview.company_name && (!!exportId || skip)
        && (!needsPassword || password.length > 0) && !busy;

    const submit = async () => {
        if (!preview || !ready) return;
        setBusy(true);
        setError(null);
        try {
            if (needsPassword) {
                if (!email) throw new LifecycleError('wrong_password', 'Sign in again to continue.');
                await reauthenticate(email, password);
            }
            const result = await applyFreshStart(context.workspace.id, preview.operation_id, name, exportId, !exportId && skip);
            onDone(result);
        } catch (e) {
            const err = e instanceof LifecycleError ? e : new LifecycleError('failed', 'That did not work. Nothing was changed.');
            setError(err.message);
            if (err.code === 'reauthentication_required') setNeedsPassword(true);
            if (['preview_stale', 'preview_expired', 'preview_missing'].includes(err.code)) await load();
            setPassword('');
        } finally {
            setBusy(false);
        }
    };

    const removed = preview ? [
        ...(['customers', 'customer_activity'] as const).map(k => [k, preview.delete[k] ?? 0] as [string, number]),
        ['promises', preview.counts['promises'] ?? 0] as [string, number],
        ...(['pdc_cheques', 'sync_runs', 'ai_report_runs'] as const).map(k => [k, preview.delete[k] ?? 0] as [string, number]),
    ] : [];
    const kept = preview ? Object.entries(preview.keep)
        .filter(([k]) => LIFECYCLE_LABELS[k] && k !== 'audit_log')
        .map(([k, n]) => [k, n] as [string, number]) : [];

    return (
        <DialogShell
            title="Fresh Start"
            subtitle="Clear the business data. Keep the company, its people and its settings."
            role="alertdialog"
            size="lg"
            onClose={onClose}
            closeOnEscape={!busy}
            footer={(
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button variant="destructive" onClick={() => { void submit(); }} disabled={!ready} data-testid="apply-fresh-start" busy={busy}>
                        {busy ? 'Clearing…' : 'Clear business data'}
                    </Button>
                </>
            )}
        >
            {loading && !preview ? (
                <div className="py-10 grid place-items-center" role="status"><Spinner className="w-6 h-6 text-accent-text" /></div>
            ) : (
                <div className="space-y-4">
                    {error && <FormNotice tone="error">{error}</FormNotice>}
                    {preview && (
                        <>
                            <p className="text-body text-label-2 leading-relaxed">
                                This removes every account in <strong className="text-label">{preview.company_name}</strong> with
                                its history, promises and cheques, all at once. It cannot be undone. Counted just now, on the server:
                            </p>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <CountList title="Will be removed" counts={removed} tone="dang" />
                                <CountList title="Will stay" counts={kept} tone="neutral" />
                            </div>
                            <p className="text-secondary text-label-3">
                                Your receivables data stays connected — TallyPrime or the sheet — so your customers come back with its next update. The audit log keeps a record of this.
                            </p>
                            <ExportStep
                                context={context}
                                exportId={exportId}
                                exportedAt={exportedAt}
                                onExported={id => { setExportId(id); setExportedAt(new Date().toISOString()); }}
                                skip={skip}
                                onSkip={setSkip}
                            />
                            <ConfirmStep
                                companyName={preview.company_name}
                                name={name}
                                onName={setName}
                                needsPassword={needsPassword}
                                password={password}
                                onPassword={setPassword}
                                step={2}
                            />
                        </>
                    )}
                </div>
            )}
        </DialogShell>
    );
}

/* --------------------------------------------------------- Delete company -- */

export function DeleteCompanyDialog({ context, onClose, onScheduled }: {
    context: TenantContext;
    onClose: () => void;
    onScheduled: (scheduledFor: string) => void;
}) {
    const { email } = useSession();
    const [requestId] = useState(() => crypto.randomUUID());
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [needsPassword, setNeedsPassword] = useState(true);
    const [exportId, setExportId] = useState<string | null>(null);
    const [exportedAt, setExportedAt] = useState<string | null>(null);
    const [skip, setSkip] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const companyName = context.workspace.name;

    const ready = name === companyName && (!!exportId || skip) && (!needsPassword || password.length > 0) && !busy;

    const submit = async () => {
        if (!ready) return;
        setBusy(true);
        setError(null);
        try {
            if (needsPassword) {
                if (!email) throw new LifecycleError('wrong_password', 'Sign in again to continue.');
                await reauthenticate(email, password);
                setNeedsPassword(false);
            }
            const result = await requestDeletion(context.workspace.id, requestId, name, exportId, !exportId && skip);
            onScheduled(result.scheduled_for);
        } catch (e) {
            const err = e instanceof LifecycleError ? e : new LifecycleError('failed', 'That did not work. Nothing was changed.');
            setError(err.message);
            if (err.code === 'reauthentication_required') setNeedsPassword(true);
            setPassword('');
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogShell
            title="Delete this company"
            subtitle={companyName}
            role="alertdialog"
            size="lg"
            onClose={onClose}
            closeOnEscape={!busy}
            footer={(
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button variant="destructive" onClick={() => { void submit(); }} disabled={!ready} data-testid="schedule-deletion" busy={busy}>
                        {busy ? 'Scheduling…' : 'Schedule deletion'}
                    </Button>
                </>
            )}
        >
            <div className="space-y-4">
                {error && <FormNotice tone="error">{error}</FormNotice>}
                <div className="rounded-control bg-dang-bg px-4 py-3.5 text-body text-label-2 leading-relaxed space-y-2">
                    <p><strong className="text-dang">{companyName} stops working at once, for everyone.</strong></p>
                    <p>
                        In seven days it is permanently deleted with everything in it: accounts, history, promises, cheques,
                        the team and its roles, settings, templates, stored keys and connections, the logo and the audit log.
                    </p>
                    <p>Until then any owner can cancel, and nothing is lost. People&rsquo;s own sign-in accounts are never deleted, and their other companies are not affected.</p>
                </div>
                <ExportStep
                    context={context}
                    exportId={exportId}
                    exportedAt={exportedAt}
                    onExported={id => { setExportId(id); setExportedAt(new Date().toISOString()); }}
                    skip={skip}
                    onSkip={setSkip}
                />
                <ConfirmStep
                    companyName={companyName}
                    name={name}
                    onName={setName}
                    needsPassword={needsPassword}
                    password={password}
                    onPassword={setPassword}
                    step={2}
                />
            </div>
        </DialogShell>
    );
}
